import { useState, useEffect, useRef, useContext } from "react";
import { C, LOGO_BASE, THUMB_BASE } from "./utils";
import { ThemeCtx, Icon } from "./AppShell";
import ReactDOM from "react-dom";

const openDocTab = (doc, subId) => {
  if (!doc?.DOCUMENTID || !doc?.DOCTYPEID) return;
  const base = window.location.origin + window.location.pathname;
  const url = `${base}?doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID}&sub=${subId||""}`;
  if(window.innerWidth < 768){
    // push current URL to history so back button works
    window.history.pushState({}, "", window.location.href);
    window.location.href = url;
  } else window.open(url, "_blank", "noopener,noreferrer");
};

const API = "https://cip.medsphere.net/api/glance_page";
const isImage   = (p="") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const fileName  = (p="") => p.split("/").pop();
const stripHtml = (h="") => h ? h.replace(/<[^>]*>/g," ").replace(/\s+/g," ").trim() : "";
const getPrimaryProduct = (a=[]) => a.find(p=>(p.PRIMARY==="1"||p.PRIMARY===1)&&String(p.BRANDID)!=="0")?.BRANDNAME||"";
const getCompanyName = c => c?.SPONSORNAME||"";
const getCompanyLogo = c => c?.COMPANY_LOGO ? LOGO_BASE+c.COMPANY_LOGO : null;

const DT = {
  95: { label:"News",    color:"#DC2626", bg:"#FEF2F2" },   // Red
  92: { label:"Reports", color:"#2563EB", bg:"#EFF6FF" },   // Blue
  94: { label:"Events",  color:"#16A34A", bg:"#F0FDF4" },   // Green
  96: { label:"Trial",   color:"#D97706", bg:"#FFFBEB" },   // Amber
  97: { label:"Profile", color:"#0891B2", bg:"#ECFEFF" },   // Cyan
};
const RECENT = { label:"Recent", color:"#6B7280", bg:"#F9FAFB" }; // Slate
const dtCfg = id => DT[Number(id)] || RECENT;

function parseDate(s) {
  if (!s) return null;
  // Handle GMT format: "Tue, 30 Jun 2026 00:00:00 GMT"
  const gmt=s.match(/(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})/);
  if(gmt){
    const M={Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11};
    return new Date(parseInt(gmt[3]),M[gmt[2]]??0,parseInt(gmt[1]));
  }
  // Handle display format: "06 Jun, 23" or "24 Feb, 26" or "09 Feb, 26"
  const disp=s.match(/(\d{1,2})\s+([A-Za-z]+)[,\s]+(\d{2,4})/);
  if(disp){
    const M={Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11};
    const yr=disp[3].length===2?2000+parseInt(disp[3]):parseInt(disp[3]);
    return new Date(yr,M[disp[2]]??0,parseInt(disp[1]));
  }
  // Handle "28 Jun 2024" from LAUNCHDATE
  const launch=s.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if(launch){
    const M={Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11};
    return new Date(parseInt(launch[3]),M[launch[2]]??0,parseInt(launch[1]));
  }
  // Handle ISO range "2025-05-01 to 2025-05-31" → use start date
  const iso=s.match(/(\d{4}-\d{2}-\d{2})/);
  if(iso) return new Date(iso[1]+"T00:00:00");
  return null;
}
const fmtShort = d => d?d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"2-digit"}):"";

function useGlanceLoader({ user, subproject }) {
  const [data,setData]=useState(null),[loading,setLoading]=useState(false),[error,setError]=useState(null);
  useEffect(()=>{
    if(!subproject||!user) return;
    setLoading(true);
    const p=new URLSearchParams({subprojectid:subproject.SUBPROJECTID,userid:user.USERID,glance:1,search:"#ataglance"});
    fetch(`${API}?${p}`,{headers:{"Auth-Code":user.AUTH_CODE}})
      .then(r=>r.json()).then(r=>{if(r.STATUS==="1")setData(r);else setError("Failed.");})
      .catch(()=>setError("Failed.")).finally(()=>setLoading(false));
  },[subproject?.SUBPROJECTID]);
  return {data,loading,error};
}

// ── Mini calendar ───────────────────────────────────────────────────────────
function MiniCal({ docs, filterDt, setFilterDt, onDayClick }) {
  const isMobile = useContext(ThemeCtx)?.isMobile || false;
  const now=new Date();
  const [yr,setYr]=useState(now.getFullYear());
  const [mo,setMo]=useState(now.getMonth());
  const first=new Date(yr,mo,1).getDay();
  const days=new Date(yr,mo+1,0).getDate();
  const MON=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const filtered=filterDt?(
    filterDt===-1
      ? docs.filter(d=>!DT[Number(d.DOCTYPEID)])  // Recent = anything not in DT
      : docs.filter(d=>d.DOCTYPEID===filterDt)
  ):docs;
  const map={};
  filtered.forEach(d=>{
    const dates=[];
    const dt=d.DOCTYPEID;

    if(dt===96){
      // Clinical Trials → STARTDATE + ENDDATE
      if(d.STARTDATE) dates.push(parseDate(d.STARTDATE));
      if(d.ENDDATE)   dates.push(parseDate(d.ENDDATE));
    } else if(dt===97||dt===98){
      // Profile / Detail → LAUNCHDATE from each PRODUCT_DETAIL entry
      (Array.isArray(d.PRODUCT_DETAIL)?d.PRODUCT_DETAIL:[]).forEach(pd=>{
        if(pd.LAUNCHDATE) dates.push(parseDate(pd.LAUNCHDATE));
      });
      // Fallback: STARTDATE / ENDDATE if no PRODUCT_DETAIL
      if(!dates.filter(Boolean).length){
        if(d.STARTDATE) dates.push(parseDate(d.STARTDATE));
        if(d.ENDDATE)   dates.push(parseDate(d.ENDDATE));
      }
    } else {
      // Events (94), News (95), Recent (99), Reports (92) → DISPLAY_TIME
      dates.push(parseDate(d.DISPLAY_TIME||""));
    }

    // Fallback for any type if still empty
    if(!dates.filter(Boolean).length) dates.push(parseDate(d.DISPLAY_TIME||""));

    dates.filter(Boolean).forEach(x=>{
      if(x.getFullYear()===yr&&x.getMonth()===mo){
        const day=x.getDate();
        if(!map[day])map[day]=[];
        if(!map[day].find(e=>e.DOCUMENTID===d.DOCUMENTID&&e._plotDate===x.toDateString()))
          map[day].push({...d,_plotDate:x.toDateString()});
      }
    });
  });
  const cells=[...Array(first).fill(null),...Array.from({length:days},(_,i)=>i+1)];
  return (
    <div style={{background:"#fff",borderRadius:"12px",border:"1px solid #E5E7EB",overflow:"hidden",display:"flex",flexDirection:"column",flex:1}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",borderBottom:"1px solid #F1F5F9"}}>
        <button onClick={()=>mo===0?(setMo(11),setYr(y=>y-1)):setMo(m=>m-1)}
          style={{border:"none",background:"none",cursor:"pointer",color:"#9CA3AF",fontSize:"14px",lineHeight:1,padding:"2px 4px"}}>‹</button>
        <span style={{fontSize:"12px",fontWeight:"700",color:"#374151"}}>{MON[mo]} {yr}</span>
        <button onClick={()=>mo===11?(setMo(0),setYr(y=>y+1)):setMo(m=>m+1)}
          style={{border:"none",background:"none",cursor:"pointer",color:"#9CA3AF",fontSize:"14px",lineHeight:1,padding:"2px 4px"}}>›</button>
      </div>
      {/* Day names */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",padding:"4px 8px 0"}}>
        {["S","M","T","W","T","F","S"].map((d,i)=>(
          <div key={i} style={{textAlign:"center",fontSize:isMobile?"10px":"8px",fontWeight:"700",color:"#CBD5E1",padding:"2px 0"}}>{d}</div>
        ))}
      </div>
      {/* Cells */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",padding:"2px 8px 8px",gap:"2px",flex:1}}>
        {cells.map((d,i)=>{
          if(!d) return <div key={i}/>;
          const evts=map[d]||[];
          const isToday=d===now.getDate()&&mo===now.getMonth()&&yr===now.getFullYear();
          const dots=[...new Set(evts.map(e=>e.DOCTYPEID))].slice(0,3);
          return (
            <div key={i} onClick={evts.length?()=>onDayClick(d,evts):undefined}
              style={{borderRadius:"5px",padding:isMobile?"6px 0":"4px 0",textAlign:"center",
                cursor:evts.length?"pointer":"default",
                background:isToday?"#6366F1":evts.length?`#6366F110`:"transparent",
                transition:"background 0.1s"}}>
              <div style={{fontSize:isMobile?"13px":"10px",fontWeight:isToday||evts.length?"700":"400",
                color:isToday?"#fff":evts.length?"#374151":"#94A3B8",lineHeight:1.4}}>{d}</div>
              {evts.length>0&&(
                <div style={{display:"flex",justifyContent:"center",gap:"2px",marginTop:"2px"}}>
                  {[...new Set(evts.map(e=>dtCfg(e.DOCTYPEID).color))].slice(0,4).map((col,j)=>(
                    <div key={j} style={{width:isMobile?"5px":"8px",height:isMobile?"5px":"8px",borderRadius:"50%",flexShrink:0,
                      background:isToday?"rgba(255,255,255,0.85)":col}}/>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Doctype filters */}
      <div style={{padding:"6px 8px",borderTop:"1px solid #F1F5F9",display:"flex",flexWrap:"wrap",gap:"4px"}}>
        {Object.entries(DT).map(([id,cfg])=>{
          const act=filterDt===parseInt(id);
          return(
            <button key={id} onClick={()=>setFilterDt(act?null:parseInt(id))}
              style={{padding:"1px 7px",borderRadius:"8px",fontSize:"9px",fontWeight:act?"700":"500",
                cursor:"pointer",border:`1px solid ${act?cfg.color:"#E5E7EB"}`,
                background:act?cfg.bg:"transparent",color:act?cfg.color:"#9CA3AF",
                display:"flex",alignItems:"center",gap:"2px"}}>
              <div style={{width:"4px",height:"4px",borderRadius:"50%",background:cfg.color,flexShrink:0}}/>
              {cfg.label}
            </button>
          );
        })}
        {/* Recent — catch-all for any other doctypeid */}
        {(()=>{const act=filterDt===-1;return(
          <button onClick={()=>setFilterDt(act?null:-1)}
            style={{padding:"1px 7px",borderRadius:"8px",fontSize:"9px",fontWeight:act?"700":"500",
              cursor:"pointer",border:`1px solid ${act?RECENT.color:"#E5E7EB"}`,
              background:act?RECENT.bg:"transparent",color:act?RECENT.color:"#9CA3AF",
              display:"flex",alignItems:"center",gap:"2px"}}>
            <div style={{width:"4px",height:"4px",borderRadius:"50%",background:RECENT.color,flexShrink:0}}/>
            {RECENT.label}
          </button>
        );})()}
      </div>
    </div>
  );
}

// ── Day popup ───────────────────────────────────────────────────────────────
function DayPopup({ day, events, month, year, tPrimary, onClose, subproject }) {
  const MON=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return ReactDOM.createPortal(
    <div style={{position:"fixed",inset:0,zIndex:9999,display:"flex",alignItems:"flex-start",justifyContent:"flex-end"}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.2)"}} onClick={onClose}/>
      <div style={{position:"relative",width:"340px",height:"100%",background:"#fff",
        boxShadow:"-8px 0 32px rgba(0,0,0,0.12)",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"14px 16px",background:`linear-gradient(135deg,${tPrimary},${tPrimary}BB)`,
          display:"flex",alignItems:"center",gap:"12px"}}>
          <div style={{background:"rgba(255,255,255,0.2)",borderRadius:"8px",padding:"4px 10px",textAlign:"center"}}>
            <div style={{fontSize:"22px",fontWeight:"900",color:"#fff",lineHeight:1}}>{day}</div>
            <div style={{fontSize:"10px",color:"rgba(255,255,255,0.8)"}}>{MON[month]}</div>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:"13px",fontWeight:"700",color:"#fff"}}>
              {(()=>{
                // Summarise what types are on this day
                const types=[...new Set(events.map(e=>e.DOCTYPENAME||"").filter(Boolean))];
                const plural = (word, n) => {
                  if(n===1) return word;
                  if(word.endsWith("s")||word.endsWith("x")||word.endsWith("ch")||word.endsWith("sh")) return word;
                  return word+"s";
                };
                if(types.length===1) return `${events.length} ${plural(types[0],events.length)}`;
                if(types.length===2) return `${types[0]} & ${types[1]}`;
                return `${events.length} updates`;
              })()}
            </div>
            <div style={{fontSize:"10px",color:"rgba(255,255,255,0.7)"}}>{MON[month]} {day}, {year}</div>
          </div>
          <button onClick={onClose} style={{border:"none",background:"rgba(255,255,255,0.2)",color:"#fff",
            width:"28px",height:"28px",borderRadius:"50%",cursor:"pointer",fontSize:"16px"}}>×</button>
        </div>
        <div style={{flex:1,overflowY:"auto"}}>
          {events.map(doc=>{
            const _coAll=Array.isArray(doc.COMPANY)?doc.COMPANY.filter(c=>String(c.SPONSORID)!=="0"):[];
            const cos=_coAll; const co=_coAll.find(c=>c.PRIMARY===1||c.PRIMARY==="1")||_coAll[0]||null;
            const cfg=dtCfg(doc.DOCTYPEID);
            const notes=stripHtml(doc.NOTES||"");
            return(
              <div key={doc.DOCUMENTID}
                onClick={()=>openDocTab(doc,subproject?.SUBPROJECTID)}
                style={{padding:"10px 14px",borderBottom:"1px solid #F1F5F9",
                  cursor:"pointer",transition:"background 0.1s"}}
                onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{display:"flex",alignItems:"center",gap:"7px",marginBottom:"5px"}}>
                  {co?.COMPANY_LOGO&&<img src={LOGO_BASE+co.COMPANY_LOGO} alt="" onError={e=>e.target.style.display="none"}
                    style={{width:"20px",height:"20px",objectFit:"contain",borderRadius:"3px",flexShrink:0}}/>}
                  <span style={{fontSize:"8px",fontWeight:"700",color:cfg.color,
                    background:cfg.bg,borderRadius:"3px",padding:"1px 5px",flexShrink:0}}>{doc.DOCTYPENAME||cfg.label}</span>
                  <span style={{fontSize:"10px",color:"#9CA3AF",flexShrink:0}}>{doc.DISPLAY_TIME}</span>
                </div>
                <div style={{fontSize:"11px",fontWeight:"600",color:"#111827",marginBottom:"3px",lineHeight:1.3}}>
                  {doc.SUBJECT}
                </div>
                {notes&&<div style={{fontSize:"10px",color:"#6B7280",lineHeight:"1.4",
                  display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
                  {notes}
                </div>}
                {doc.KEYWORDS&&(
                  <div style={{display:"flex",flexWrap:"wrap",gap:"3px",marginTop:"4px"}}>
                    {doc.KEYWORDS.split(",").map(t=>t.trim()).filter(Boolean).map((t,i)=>(
                      <span key={i} style={{fontSize:"8px",fontWeight:"600",color:cfg.color,
                        background:`${cfg.color}12`,borderRadius:"8px",padding:"1px 5px"}}>
                        {t.replace(/^#/,"")}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── NewsFlash card ──────────────────────────────────────────────────────────
function NLCard({ doc, tPrimary, subId, onOpen }) {

  const rels=Array.isArray(doc.RELATIVE)?doc.RELATIVE:[];
  const d=parseDate(doc.DISPLAY_TIME);
  const notes=stripHtml(doc.NOTES||"");
  return(
    <div style={{borderBottom:"1px solid #F1F5F9",transition:"background 0.1s"}}>
      {/* Header row — click to open in new tab */}
      <div style={{padding:"10px 14px",display:"flex",alignItems:"flex-start",gap:"10px"}}>
        {/* Date badge */}
        {d&&(
          <div style={{flexShrink:0,width:"36px",textAlign:"center",
            background:`${tPrimary}12`,borderRadius:"6px",padding:"4px 0"}}>
            <div style={{fontSize:"15px",fontWeight:"900",color:tPrimary,lineHeight:1}}>{d.getDate()}</div>
            <div style={{fontSize:"8px",fontWeight:"700",color:`${tPrimary}88`}}>
              {d.toLocaleDateString("en-GB",{month:"short"})}
            </div>
          </div>
        )}
        {/* Title + meta — click opens detail */}
        <div onClick={()=>onOpen&&onOpen(doc)} style={{flex:1,minWidth:0,cursor:"pointer"}}>
          <div style={{fontSize:"12px",fontWeight:"700",color:"#111827",lineHeight:1.3,
            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{doc.SUBJECT}</div>
          {doc.DISPLAY_TIME&&<div style={{fontSize:"10px",color:"#9CA3AF",marginTop:"1px"}}>{doc.DISPLAY_TIME}</div>}
          {notes&&<div style={{fontSize:"11px",color:"#6B7280",lineHeight:1.5,marginTop:"3px",
            display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
            {notes}
          </div>}
        </div>

      </div>
      {/* Related docs — expandable */}
      {rels.length>0&&(
        <div style={{borderTop:"1px solid #F1F5F9",background:`${tPrimary}03`}}>
          {rels.map(r=>{
            const rc=dtCfg(r.DOCTYPEID);
            return(
              <div key={r.DOCUMENTID}
                onClick={()=>openDocTab(r,subId)}
                onMouseEnter={e=>e.currentTarget.style.background=`${tPrimary}08`}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                style={{display:"flex",alignItems:"center",gap:"7px",
                  padding:"6px 14px 6px 24px",borderBottom:"1px solid #F8FAFC",
                  cursor:"pointer",transition:"background 0.1s"}}>
                {r.COMPANY_LOGO&&<img src={LOGO_BASE+r.COMPANY_LOGO} alt=""
                  onError={e=>e.target.style.display="none"}
                  style={{width:"16px",height:"16px",objectFit:"contain",borderRadius:"3px",flexShrink:0}}/>}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:"10px",fontWeight:"600",color:"#374151",
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {r.TITLE||r.BRANDNAME||r.SPONSORNAME||"—"}
                  </div>
                  {r.SPONSORNAME&&<div style={{fontSize:"8px",color:"#9CA3AF"}}>{r.SPONSORNAME}</div>}
                </div>
                <span style={{fontSize:"8px",fontWeight:"700",color:rc.color,
                  background:rc.bg,borderRadius:"3px",padding:"1px 4px",flexShrink:0}}>
                  {r.DOCTYPENAME||rc.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Doc tile (for bottom 3 columns) ────────────────────────────────────────
function DocTile({ doc, tPrimary, onClick }) {
  const [hov,setHov]=useState(false);
  const _coAll=Array.isArray(doc.COMPANY)?doc.COMPANY.filter(c=>String(c.SPONSORID)!=="0"):[];
  const cos=_coAll;
  const co=_coAll.find(c=>c.PRIMARY===1||c.PRIMARY==="1")||_coAll[0]||null;
  const logoUrl=getCompanyLogo(co);
  const sponsor=getCompanyName(co);
  const product=getPrimaryProduct(Array.isArray(doc.PRODUCTS)?doc.PRODUCTS:[]);
  const chatbot=Array.isArray(doc.CHATBOT)?doc.CHATBOT:[];
  const thumb=chatbot.filter(a=>isImage(a.FILEPATH))[0];
  const thumbUrl=thumb?THUMB_BASE+fileName(thumb.FILEPATH):null;
  const cfg=dtCfg(doc.DOCTYPEID);

  return(
    <div onClick={onClick}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{display:"flex",alignItems:"center",gap:"8px",padding:"7px 10px",
        borderRadius:"7px",cursor:"pointer",transition:"background 0.1s",
        background:hov?`${tPrimary}07`:"transparent",
        borderBottom:"1px solid #F8FAFC"}}>
      {/* Logo / thumb */}
      <div style={{width:"32px",height:"32px",borderRadius:"6px",flexShrink:0,overflow:"hidden",
        border:`1px solid ${cfg.color}22`,background:cfg.bg,
        display:"flex",alignItems:"center",justifyContent:"center"}}>
        {logoUrl
          ?<img src={logoUrl} alt="" onError={e=>e.target.style.display="none"}
            style={{width:"100%",height:"100%",objectFit:"contain"}}/>
          :thumbUrl
            ?<img src={thumbUrl} alt="" onError={e=>e.target.style.display="none"}
              style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            :<span style={{fontSize:"10px",fontWeight:"800",color:cfg.color}}>
              {(sponsor||"?")[0]}
            </span>}
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:"11px",fontWeight:"600",color:"#111827",
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",lineHeight:1.3}}>
          {product&&<span style={{color:tPrimary}}>{product}: </span>}
          {doc.SUBJECT||"Untitled"}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:"5px",marginTop:"1px"}}>
          {sponsor&&<span style={{fontSize:"9px",color:"#9CA3AF",overflow:"hidden",
            textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"90px"}}>{sponsor}</span>}
          {doc.DISPLAY_TIME&&<span style={{fontSize:"9px",color:"#CBD5E1",flexShrink:0}}>·</span>}
          {doc.DISPLAY_TIME&&<span style={{fontSize:"9px",color:"#9CA3AF",flexShrink:0}}>{doc.DISPLAY_TIME}</span>}
        </div>
      </div>
      {/* Right stats */}
      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",flexShrink:0,gap:"2px"}}>
        {[{n:"attach",v:doc.COUNT},{n:"chat",v:doc.COMMENTCOUNT},{n:"like",v:doc.LIKES}].map(s=>
          s.v>0 ? (
            <div key={s.n} style={{display:"flex",alignItems:"center",gap:"2px",
              fontSize:"10px",color:"#9CA3AF"}}>
              <Icon name={s.n} size={11} color="#9CA3AF"/><span>{s.v}</span>
            </div>
          ) : null
        )}
      </div>
    </div>
  );
}

// ── Section strip ───────────────────────────────────────────────────────────
function Strip({ title, icon, color, bg, onViewAll, children, style }) {
  return(
    <div style={{background:"#fff",borderRadius:"12px",border:"1px solid #E5E7EB",
      overflow:"hidden",display:"flex",flexDirection:"column",
      boxShadow:"0 1px 4px rgba(0,0,0,0.04)",...(style||{})}}>
      <div style={{display:"flex",alignItems:"center",gap:"7px",padding:"6px 12px",
        background:`linear-gradient(90deg,${bg},#fff)`,borderBottom:`1px solid ${color}22`,flexShrink:0}}>
        <span style={{fontSize:"13px"}}>{icon}</span>
        <button onClick={onViewAll}
          style={{fontSize:"12px",fontWeight:"700",color,background:"none",border:"none",
            cursor:"pointer",display:"flex",alignItems:"center",gap:"3px",padding:0}}>
          {title} <span style={{fontSize:"13px",lineHeight:1}}>›</span>
        </button>
      </div>
      <div style={{flex:1,overflowY:"auto"}}>{children}</div>
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────
export default function AtAGlance({ user, subproject, onNavigate }) {
  const {data,loading,error}=useGlanceLoader({user,subproject});
  const [filterDt,setFilterDt]=useState(null);
  const [dayPanel,setDayPanel]=useState(null);
  const ctx=useContext(ThemeCtx)||{};
  const theme=ctx.theme||ctx||{};
  const tPrimary=theme.primary||C.primary;
  const isMobile=ctx.isMobile||false;

  const newsletter=data?.NEWSLETTER||[];
  const news=data?.NEWS||[];
  const latestComments=data?.LATEST_COMMENTS||[];
  const reports=data?.REPORTS||[];
  const glance=data?.GLANCE||[];   // trials + profiles + detail
  const logo=data?.LOGO||null;

  // Calendar uses GLANCE for trials/profiles + NEWS/LATEST_COMMENTS/REPORTS for events/news/recent
  const allDocs=[...news,...latestComments,...reports,...glance];

  if(loading) return(
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",
      height:"100%",gap:"8px",color:"#9CA3AF",fontSize:"13px"}}>
      <div style={{width:"14px",height:"14px",border:`2px solid #E5E7EB`,
        borderTopColor:tPrimary,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
      Loading...
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
  if(error)  return <div style={{padding:"20px",color:"#DC2626",fontSize:"13px"}}>{error}</div>;
  if(!data)  return null;

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",
      overflow:"hidden",background:"#F0F4F8",boxSizing:"border-box"}}>
      {/* ══ TOP ROW — desktop only ═══════════════════════════════════════════ */}
      {!isMobile&&<div style={{display:"flex",flexDirection:"row",
        gap:"12px",padding:"12px 14px 6px",flex:1,minHeight:0}}>

        {/* NewsFlash panel — desktop only, mobile shows as strip below */}
        {!isMobile&&<div style={{flex:"0 0 42%",
          background:"#fff",borderRadius:"12px",
          border:"1px solid #E5E7EB",overflow:"hidden",display:"flex",flexDirection:"column",
          boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
          {/* Header with dynamic LOGO */}
          <div style={{background:`linear-gradient(135deg,${tPrimary}EE,${tPrimary}99)`,
            padding:"10px 14px",display:"flex",alignItems:"center",gap:"10px"}}>
            {logo
              ?<img src={logo} alt="" onError={e=>e.target.style.display="none"}
                  style={{height:"28px",objectFit:"contain",maxWidth:"80px",
                    background:"rgba(255,255,255,0.15)",borderRadius:"5px",padding:"2px 6px"}}/>
              :<div style={{width:"28px",height:"28px",borderRadius:"7px",
                  background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",
                  justifyContent:"center",color:"#fff",fontWeight:"900",fontSize:"14px"}}>P</div>}
            <div style={{flex:1,minWidth:0}}>
              <button onClick={()=>onNavigate?.("newsletter")}
                style={{background:"none",border:"none",cursor:"pointer",padding:0,
                  display:"flex",alignItems:"center",gap:"3px"}}>
                <span style={{fontSize:"13px",fontWeight:"800",color:"#fff"}}>NewsFlash</span>
                <span style={{fontSize:"13px",color:"rgba(255,255,255,0.7)"}}>›</span>
              </button>
              <div style={{fontSize:"9px",color:"rgba(255,255,255,0.75)"}}>
                {newsletter.length} edition{newsletter.length!==1?"s":""}
              </div>
            </div>
          </div>
          {/* List */}
          <div style={{flex:1,overflowY:"auto"}}>
            {newsletter.length===0
              ?<div style={{padding:"2rem",textAlign:"center",color:"#9CA3AF",fontSize:"12px"}}>No newsletters</div>
              :newsletter.map(doc=><NLCard key={doc.DOCUMENTID} doc={doc} tPrimary={tPrimary} subId={subproject?.SUBPROJECTID} onOpen={d=>openDocTab(d,subproject?.SUBPROJECTID)}/>)}
          </div>
        </div>}

        {/* Mini calendar — desktop only */}
        {!isMobile&&<div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column"}}>
          <MiniCal docs={allDocs} filterDt={filterDt} setFilterDt={setFilterDt}
            onDayClick={(d,e)=>{
              const now=new Date();
              setDayPanel({day:d,events:e,month:now.getMonth(),year:now.getFullYear()});
            }}/>
        </div>}
      </div>}

      {/* ══ BOTTOM STRIPS ══════════════════════════════════════════════════════ */}
      <div style={{flexShrink:isMobile?0:0,flex:isMobile?1:undefined,
        display:isMobile?"flex":"grid",
        flexDirection:isMobile?"column":undefined,
        gridTemplateColumns:isMobile?undefined:"1fr 1fr 1fr",
        gap:"12px",padding:"6px 14px 12px",overflowY:isMobile?"auto":"hidden",
        height:isMobile?"auto":"220px"}}>

        {/* NewsFlash — same style as News on mobile */}
        {isMobile ? (
          <Strip title="NewsFlash" icon="📰" color={tPrimary} bg={`${tPrimary}10`}
            onViewAll={()=>onNavigate?.("newsletter")}
            style={{height:"110px"}}>
            {newsletter.length===0
              ?<div style={{padding:"2rem",textAlign:"center",color:"#9CA3AF",fontSize:"12px"}}>No newsletters</div>
              :newsletter.map(doc=><NLCard key={doc.DOCUMENTID} doc={doc} tPrimary={tPrimary} subId={subproject?.SUBPROJECTID} onOpen={d=>openDocTab(d,subproject?.SUBPROJECTID)}/>)}
          </Strip>
        ) : null}

        <Strip title="News" icon="📢" color={DT[95].color} bg={DT[95].bg}
          onViewAll={()=>onNavigate?.("news")}
          style={isMobile?{height:"240px"}:undefined}>
          {news.length===0
            ?<div style={{padding:"2rem",textAlign:"center",color:"#9CA3AF",fontSize:"12px"}}>No news</div>
            :news.map(doc=><DocTile key={doc.DOCUMENTID} doc={doc} tPrimary={tPrimary} onClick={()=>openDocTab(doc,subproject?.SUBPROJECTID)}/>)}
        </Strip>

        {/* Latest Commented — desktop only */}
        {!isMobile&&(
          <Strip title="Latest Commented" icon="💬" color={DT[94].color} bg={DT[94].bg}
            onViewAll={()=>onNavigate?.("gallery","comment")}>
            {latestComments.length===0
              ?<div style={{padding:"2rem",textAlign:"center",color:"#9CA3AF",fontSize:"12px"}}>No comments</div>
              :latestComments.map(doc=><DocTile key={doc.DOCUMENTID} doc={doc} tPrimary={tPrimary} onClick={()=>openDocTab(doc,subproject?.SUBPROJECTID)}/>)}
          </Strip>
        )}

        <Strip title="Reports" icon="📄" color={DT[92].color} bg={DT[92].bg}
          onViewAll={()=>onNavigate?.("reports")}
          style={isMobile?{height:"240px"}:undefined}>
          {reports.length===0
            ?<div style={{padding:"2rem",textAlign:"center",color:"#9CA3AF",fontSize:"12px"}}>No reports</div>
            :reports.map(doc=><DocTile key={doc.DOCUMENTID} doc={doc} tPrimary={tPrimary} onClick={()=>openDocTab(doc,subproject?.SUBPROJECTID)}/>)}
        </Strip>

      </div>

      {/* Day popup */}
      {dayPanel&&(
        <DayPopup day={dayPanel.day} events={dayPanel.events}
          month={dayPanel.month} year={dayPanel.year}
          tPrimary={tPrimary} onClose={()=>setDayPanel(null)}
          subproject={subproject}/>
      )}
    </div>
  );
}